/**
 * Grouped options for PartyTemplate role pickers (users / teams / units / roles).
 * Shared by assets, risk-register, document ownership UIs.
 *
 * Group index contract (used by asset-party-ownership.js): 0 users, 1 teams, 2 units, 3 roles.
 * Each group also carries `groupKey` so filters can find units after a group is removed.
 *
 * Labels live under `common:ownerGroups` (core namespace) so group headings resolve even when
 * feature pages never load `assets`.
 */
import i18next from 'i18next';
import { indexUnitOptions } from './unit-hierarchy.js';

/** Stable empty groups so Zustand selectors do not allocate `[]` every render. */
export const EMPTY_OWNER_GROUPS = Object.freeze([]);

export const DEFAULT_OWNER_GROUPS_I18N_PREFIX = 'common:ownerGroups';

const OWNER_GROUP_DEFAULTS = Object.freeze({
    users: 'Users',
    teams: 'Teams',
    units: 'Units',
    roles: 'Roles',
});

/** Unit ids from a user row (memberships.unit, unitId, or unit).
 * @param {object} user
 * @returns {string[]}
 */
export function collectUserUnitIds(user) {
    const ids = new Set();
    if (!user || typeof user !== 'object') return [];
    for (const membership of Array.isArray(user.memberships) ? user.memberships : []) {
        const unit = membership?.unit;
        if (unit == null || unit === '') continue;
        ids.add(String(unit._id || unit.id || unit));
    }
    if (user.unitId) ids.add(String(user.unitId));
    if (user.unit != null && user.unit !== '') {
        ids.add(String(user.unit._id || user.unit.id || user.unit));
    }
    return [...ids];
}

/**
 * @param {object | null | undefined} dropdownData
 * @returns {any[]}
 */
export function ownerGroupsFromDropdownData(dropdownData) {
    return Array.isArray(dropdownData?.owners) ? dropdownData.owners : EMPTY_OWNER_GROUPS;
}

/**
 * @param {{ users?: object[], teams?: object[], units?: object[], roles?: object[] }} entities
 * @param {{ i18nPrefix?: string }} [opts] - e.g. `common:ownerGroups`
 */
export function buildOwnerGroupedOptions(entities = {}, opts = {}) {
    const prefix = opts.i18nPrefix || DEFAULT_OWNER_GROUPS_I18N_PREFIX;
    const users = Array.isArray(entities.users) ? entities.users : [];
    const teams = Array.isArray(entities.teams) ? entities.teams : [];
    const units = Array.isArray(entities.units) ? entities.units : [];
    const roles = Array.isArray(entities.roles) ? entities.roles : [];

    const { ordered: unitNodes } = indexUnitOptions(units);

    return [
        {
            label: i18next.t(`${prefix}.users`, { defaultValue: OWNER_GROUP_DEFAULTS.users }),
            groupKey: 'users',
            options: users.map((u) => ({
                value: u._id || u.id,
                label: (u.firstName || u.lastName)
                    ? `${u.firstName || ''} ${u.lastName || ''}`.trim()
                    : (u.name || u.userName || u.email || 'Unnamed User'),
                unitIds: collectUserUnitIds(u),
            })),
        },
        {
            label: i18next.t(`${prefix}.teams`, { defaultValue: OWNER_GROUP_DEFAULTS.teams }),
            groupKey: 'teams',
            options: teams.map((t) => ({
                value: t._id || t.id,
                label: t.name || t.teamName || t.label || 'Unnamed Team',
            })),
        },
        {
            label: i18next.t(`${prefix}.units`, { defaultValue: OWNER_GROUP_DEFAULTS.units }),
            groupKey: 'units',
            options: unitNodes.map((node) => ({
                value: node.id,
                label: node.label,
                kind: 'unit',
                parentId: node.parentId,
                depth: node.depth,
                hasChildren: node.hasChildren,
                ancestorIds: node.ancestorIds,
                code: node.code,
                status: node.status,
                pathLabel: node.pathLabel,
            })),
        },
        {
            label: i18next.t(`${prefix}.roles`, { defaultValue: OWNER_GROUP_DEFAULTS.roles }),
            groupKey: 'roles',
            options: roles.map((r) => ({
                value: r._id || r.id,
                label: r.name || r.roleName || r.label || 'Unnamed Role',
            })),
        },
    ];
}
